import { useState, useEffect, useContext } from 'react';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { API_URL } from '../../Constants';
import { AuthContext } from '../context/AuthContext';

const AutoReply = () => {
  const { user, token } = useContext(AuthContext);
  const [formData, setFormData] = useState({
    receivedText: '',
    replyText: '',
    fileType: 'text', // Default to text
    caption: '',
    templateName: '',
    file: null
  });
  const [autoReplies, setAutoReplies] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [deleteModal, setDeleteModal] = useState({ isOpen: false, id: null });
  const [loading, setLoading] = useState(false);
  const [templates, setTemplates] = useState([]);
  const companyId = user?.accountType === "COMPANY" ? user?.id : user?.companyId;

  // Common headers for API requests
  const getHeaders = () => ({
    'Content-Type': 'application/json',
    Authorization: `Bearer ${localStorage.getItem("token")}`  ,
  });

  // Fetch templates
  const fetchTemplates = async () => {
    try {
      const response = await fetch(`${API_URL}/api/templates/${companyId}`, {
        headers: getHeaders()
      });
      
      if (response.ok) {
        const data = await response.json();
        setTemplates(data || []);
      } else {
        throw new Error('Failed to fetch templates');
      }
    } catch (error) {
      console.error('Error fetching templates:', error);
    }
  };

  // Fetch all auto-replies
  const fetchAutoReplies = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/api/auto-reply/getAll?companyId=${companyId}`, {
        headers: getHeaders()
      });
      
      if (response.ok) {
        const data = await response.json();
        setAutoReplies(data);
      } else {
        throw new Error('Failed to fetch auto-replies');
      }
    } catch (error) {
      toast.error('Error fetching auto-replies: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  // Create new auto-reply
  const getFileType = (file) => {
  if (!file) return 'document';
  
  const fileType = file.type;
  
  if (fileType.startsWith('image/')) {
    return 'image';
  } else if (fileType.startsWith('video/')) {
    return 'video';
  } else {
    return 'document';
  }
};

// Update the handleSubmit function
const handleSubmit = async (e) => {
  e.preventDefault();
  
  // Validation based on fileType
  if (!formData.receivedText.trim()) {
    toast.error('Please fill in Received Text field');
    return;
  }

  if (formData.fileType === 'text' && !formData.replyText.trim()) {
    toast.error('Please fill in Reply Text field');
    return;
  }

  if (formData.fileType === 'upload' && !formData.file) {
    toast.error('Please select a file to upload');
    return;
  }

  if (formData.fileType === 'template' && !formData.templateName) {
    toast.error('Please select a template');
    return;
  }

  try {
    setLoading(true);
    
    // Create FormData for file upload
    const submitData = new FormData();
    submitData.append('companyId', companyId);
    submitData.append('receivedText', formData.receivedText.trim());
    
    // Determine replyType based on fileType selection
    let replyType = formData.fileType;
    
    // If fileType is upload, detect the actual file type
    if (formData.fileType === 'upload' && formData.file) {
      replyType = getFileType(formData.file);
    }
    
    submitData.append('replyType', replyType);
    
    // Append fields based on fileType
    if (formData.fileType === 'text') {
      submitData.append('replyText', formData.replyText.trim());
    } else if (formData.fileType === 'upload') {
      submitData.append('file', formData.file);
      submitData.append('caption', formData.caption || '');
    } else if (formData.fileType === 'template') {
      submitData.append('templateName', formData.templateName);
    }

    const response = await fetch(`${API_URL}/api/auto-reply/create`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
        // Don't set Content-Type for FormData, browser will set it with boundary
      },
      body: submitData
    });

    if (response.ok) {
      toast.success('Auto-reply created successfully!');
      // Reset form
      setFormData({
        receivedText: '',
        replyText: '',
        fileType: 'text',
        caption: '',
        templateName: '',
        file: null
      });
      fetchAutoReplies();
    } else {
      throw new Error('Failed to create auto-reply');
    }
  } catch (error) {
    toast.error('Error creating auto-reply: ' + error.message);
  } finally {
    setLoading(false);
  }
};

// Fetch auto-reply for editing
  const handleEdit = async (id) => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/api/auto-reply/getById/${id}?companyId=${companyId}`, {
        headers: getHeaders()
      });
      
      if (response.ok) {
        const data = await response.json();
        setFormData({
          receivedText: data.receivedText,
          replyText: data.replyText || '',
          fileType: data.replyType || 'text',
          caption: data.caption || '',
          templateName: data.templateName || '',
          file: null
        });
        setEditingId(id);
        // Scroll to form
        document.getElementById('autoReplyForm').scrollIntoView({ behavior: 'smooth' });
      } else {
        throw new Error('Failed to fetch auto-reply');
      }
    } catch (error) {
      toast.error('Error fetching auto-reply: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

// Update the handleUpdate function similarly
const handleUpdate = async (e) => {
  e.preventDefault();
  
  // Validation based on fileType
  if (!formData.receivedText.trim()) {
    toast.error('Please fill in Received Text field');
    return;
  }

  if (formData.fileType === 'text' && !formData.replyText.trim()) {
    toast.error('Please fill in Reply Text field');
    return;
  }

  if (formData.fileType === 'upload' && !formData.file) {
    toast.error('Please select a file to upload');
    return;
  }

  if (formData.fileType === 'template' && !formData.templateName) {
    toast.error('Please select a template');
    return;
  }

  try {
    setLoading(true);
    
    // Create FormData for file upload
    const submitData = new FormData();
    submitData.append('companyId', companyId);
    submitData.append('receivedText', formData.receivedText.trim());
    
    // Determine replyType based on fileType selection
    let replyType = formData.fileType;
    
    // If fileType is upload, detect the actual file type
    if (formData.fileType === 'upload' && formData.file) {
      replyType = getFileType(formData.file);
    }
    
    submitData.append('replyType', replyType);
    
    // Append fields based on fileType
    if (formData.fileType === 'text') {
      submitData.append('replyText', formData.replyText.trim());
    } else if (formData.fileType === 'upload') {
      submitData.append('file', formData.file);
      submitData.append('caption', formData.caption || '');
    } else if (formData.fileType === 'template') {
      submitData.append('templateName', formData.templateName);
    }

    const response = await fetch(`${API_URL}/api/auto-reply/update/${editingId}?companyId=${companyId}`, {
      method: 'PUT',
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
      body: submitData
    });

    if (response.ok) {
      toast.success('Auto-reply updated successfully!');
      // Reset form
      setFormData({
        receivedText: '',
        replyText: '',
        fileType: 'text',
        caption: '',
        templateName: '',
        file: null
      });
      setEditingId(null);
      fetchAutoReplies();
    } else {
      throw new Error('Failed to update auto-reply');
    }
  } catch (error) {
    toast.error('Error updating auto-reply: ' + error.message);
  } finally {
    setLoading(false);
  }
};

  // Delete auto-reply
  const handleDelete = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/api/auto-reply/delete/${deleteModal.id}?companyId=${companyId}`, {
        method: 'DELETE',
        headers: getHeaders()
      });

      if (response.ok) {
        toast.success('Auto-reply deleted successfully!');
        setDeleteModal({ isOpen: false, id: null });
        fetchAutoReplies();
      } else {
        throw new Error('Failed to delete auto-reply');
      }
    } catch (error) {
      toast.error('Error deleting auto-reply: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  // Cancel edit
  const handleCancelEdit = () => {
    setFormData({
      receivedText: '',
      replyText: '',
      fileType: 'text',
      caption: '',
      templateName: '',
      file: null
    });
    setEditingId(null);
  };

  // Open delete confirmation modal
  const openDeleteModal = (id) => {
    setDeleteModal({ isOpen: true, id });
  };

  // Close delete confirmation modal
  const closeDeleteModal = () => {
    setDeleteModal({ isOpen: false, id: null });
  };

  // Handle file input change
  const handleFileChange = (e) => {
    setFormData({ ...formData, file: e.target.files[0] });
  };

  // Fetch auto-replies and templates on component mount
  useEffect(() => {
    if (companyId) {
      fetchAutoReplies();
      fetchTemplates();
    }
  }, [companyId]);

  // Show loading if companyId is not available yet
  if (!companyId) {
    return (
      <div className="min-h-screen bg-gray-50 py-8 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="text-gray-600 mt-2">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Auto Reply Management</h1>
          <p className="text-gray-600 mt-2">Manage automatic responses for received messages</p>
        </div>

        {/* Auto Reply Form */}
        <div id="autoReplyForm" className="bg-white rounded-lg shadow-md p-6 mb-8">
          <h2 className="text-xl font-semibold text-gray-800 mb-4">
            {editingId ? 'Edit Auto Reply' : 'Create New Auto Reply'}
          </h2>
          
          <form onSubmit={editingId ? handleUpdate : handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="receivedText" className="block text-sm font-medium text-gray-700 mb-1">
                  Received Text *
                </label>
                <input
                  type="text"
                  id="receivedText"
                  value={formData.receivedText}
                  onChange={(e) => setFormData({ ...formData, receivedText: e.target.value })}
                  placeholder="Enter the text that triggers this auto-reply"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                />
              </div>
              
              <div>
                <label htmlFor="fileType" className="block text-sm font-medium text-gray-700 mb-1">
                  Reply Type *
                </label>
                <select
                  id="fileType"
                  value={formData.fileType}
                  onChange={(e) => setFormData({ ...formData, fileType: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                >
                  <option value="text">Text</option>
                  <option value="upload">Upload</option>
                  <option value="template">Template</option>
                </select>
              </div>
            </div>

            {/* Conditional Fields based on fileType */}
            {formData.fileType === 'text' && (
              <div>
                <label htmlFor="replyText" className="block text-sm font-medium text-gray-700 mb-1">
                  Reply Text *
                </label>
                <input
                  type="text"
                  id="replyText"
                  value={formData.replyText}
                  onChange={(e) => setFormData({ ...formData, replyText: e.target.value })}
                  placeholder="Enter the automatic reply text"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                />
              </div>
            )}

                {formData.fileType === 'upload' && (
  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
    <div>
      <label htmlFor="file" className="block text-sm font-medium text-gray-700 mb-1">
        File *
      </label>
      <input
        type="file"
        id="file"
        onChange={handleFileChange}
        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        required={formData.fileType === 'upload'}
        accept="image/*,video/*,.pdf,.doc,.docx,.txt" // Optional: restrict file types
      />
      {formData.file && (
        <div className="mt-1">
          <p className="text-sm text-green-600">Selected: {formData.file.name}</p>
          <p className="text-sm text-blue-600">
            Detected as: {getFileType(formData.file)}
          </p>
        </div>
      )}
    </div>
    <div>
      <label htmlFor="caption" className="block text-sm font-medium text-gray-700 mb-1">
        Caption
      </label>
      <input
        type="text"
        id="caption"
        value={formData.caption}
        onChange={(e) => setFormData({ ...formData, caption: e.target.value })}
        placeholder="Enter caption for the file"
        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
      />
    </div>
  </div>
)}

            {formData.fileType === 'template' && (
              <div>
                <label htmlFor="templateName" className="block text-sm font-medium text-gray-700 mb-1">
                  Template Name *
                </label>
                <select
                  id="templateName"
                  value={formData.templateName}
                  onChange={(e) => setFormData({ ...formData, templateName: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required={formData.fileType === 'template'}
                >
                  <option value="">Select a template</option>
                  {templates.map((template) => (
                    <option key={template.id} value={template.name}>
                      {template.name}
                    </option>
                  ))}
                </select>
              </div>
            )}
            
            <div className="flex space-x-3 pt-2">
              <button
                type="submit"
                disabled={loading}
                className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {loading ? 'Processing...' : (editingId ? 'Update Auto Reply' : 'Create Auto Reply')}
              </button>
              
              {editingId && (
                <button
                  type="button"
                  onClick={handleCancelEdit}
                  disabled={loading}
                  className="px-6 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 disabled:opacity-50 transition-colors"
                >
                  Cancel
                </button>
              )}
            </div>
          </form>
        </div>

        {/* Auto Replies Table */}
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-xl font-semibold text-gray-800">Auto Replies List</h2>
          </div>
          
          {loading && autoReplies.length === 0 ? (
            <div className="p-8 text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
              <p className="text-gray-600 mt-2">Loading auto-replies...</p>
            </div>
          ) : autoReplies.length === 0 ? (
            <div className="p-8 text-center">
              <p className="text-gray-600">No auto-replies found. Create your first one above!</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Received Text
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Reply Type
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Content
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {autoReplies.map((reply) => (
                    <tr key={reply.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">{reply.receivedText}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900 capitalize">{reply.replyType}</div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-900">
                          {reply.replyType === 'text' && reply.replyText}
                          {reply.replyType === 'upload' && `File: ${reply.fileName || 'N/A'} ${reply.caption ? `- ${reply.caption}` : ''}`}
                          {reply.replyType === 'template' && `Template: ${reply.templateName}`}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex space-x-2">
                          <button
                            onClick={() => handleEdit(reply.id)}
                            disabled={loading}
                            className="text-blue-600 hover:text-blue-900 disabled:opacity-50 transition-colors"
                            title="Edit"
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                            </svg>
                          </button>
                          <button
                            onClick={() => openDeleteModal(reply.id)}
                            disabled={loading}
                            className="text-red-600 hover:text-red-900 disabled:opacity-50 transition-colors"
                            title="Delete"
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {deleteModal.isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Confirm Deletion</h3>
            <p className="text-gray-600 mb-6">
              Are you sure you want to delete this auto-reply? This action cannot be undone.
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={closeDeleteModal}
                disabled={loading}
                className="px-4 py-2 text-gray-600 hover:text-gray-800 disabled:opacity-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                disabled={loading}
                className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 disabled:opacity-50 transition-colors"
              >
                {loading ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AutoReply;